import { ref, onMounted } from 'vue'
import { parseRepoSlug } from '@/integrations/github'

export interface ExtensionRelease {
  version: string
  tagName: string
  downloadUrl: string | null
  publishedAt: string | null
}

// Public repo with releases so the page works without env. Set VITE_GITHUB_EXTENSION_REPO to your repo (e.g. GoHighLevel/ghl-ai-test-generator); use VITE_GITHUB_TOKEN if it's private.
const DEFAULT_REPO = 'GoHighLevel/ghl-ai-test-generator'

export function useChromeExtensionReleases(maxReleases = 4) {
  const versions = ref<ExtensionRelease[]>([])
  const loading = ref(true)
  const error = ref<string | null>(null)

  async function fetchReleases() {
    const repo = import.meta.env.VITE_GITHUB_EXTENSION_REPO || DEFAULT_REPO
    const parsed = parseRepoSlug(repo)
    if (!parsed) {
      error.value = 'Invalid GitHub repo (use owner/repo)'
      loading.value = false
      return
    }

    try {
      loading.value = true
      error.value = null
      const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL
      const apiBaseUrl = (configuredBaseUrl && configuredBaseUrl.trim())
        ? configuredBaseUrl.trim()
        : 'https://dev.services.leadconnectorhq.com/hltestmanager'
      const endpointPath = `/github/releases?owner=${encodeURIComponent(parsed.owner)}&repo=${encodeURIComponent(parsed.repo)}&perPage=${maxReleases}`
      const endpoint = `${apiBaseUrl}${endpointPath}`
      const response = await fetch(endpoint)
      if (!response.ok) {
        let bodyText = ''
        try {
          bodyText = await response.clone().text()
        } catch {
          bodyText = ''
        }
        throw new Error(`Failed to fetch releases (${response.status})`)
      }
      const payload = await response.json() as {
        releases?: Array<ExtensionRelease>
      }
      versions.value = Array.isArray(payload.releases)
        ? payload.releases.map((release) => ({
            ...release,
            downloadUrl: release.downloadUrl?.startsWith('/')
              ? `${apiBaseUrl}${release.downloadUrl.startsWith('/hltestmanager/') ? release.downloadUrl.replace('/hltestmanager', '') : release.downloadUrl}`
              : release.downloadUrl ?? null
          }))
        : []
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch releases'
      const repoUrl = `https://github.com/${parsed.owner}/${parsed.repo}`
      error.value = `${message} Repo: ${repo} (${repoUrl}). If private, set VITE_GITHUB_TOKEN in .env.`
      versions.value = []
    } finally {
      loading.value = false
    }
  }

  onMounted(fetchReleases)

  return { versions, loading, error, fetchReleases }
}
